/**
 * Contact Notes Service
 * CRUD operations for journal-style contact notes
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface ContactNote {
  id: string;
  contact_id: string;
  content: string;
  support_task_id?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  creator?: {
    full_name: string;
    email: string;
  };
}

/**
 * Get all notes for a contact
 */
export const getContactNotes = async (
  contactId: UUID,
): Promise<ContactNote[]> => {
  const { data, error } = await supabase
    .from("contact_notes")
    .select(
      `
      *,
      creator:profiles!contact_notes_created_by_fkey(full_name, email)
    `,
    )
    .eq("contact_id", contactId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching contact notes:", error);
    throw error;
  }

  return data || [];
};

/**
 * Create a new contact note
 */
export const createContactNote = async (
  contactId: UUID,
  content: string,
  supportTaskId?: UUID,
  createdBy?: UUID,
): Promise<ContactNote> => {
  const { data, error } = await supabase
    .from("contact_notes")
    .insert({
      contact_id: contactId,
      content,
      support_task_id: supportTaskId || null,
      created_by: createdBy || null,
    })
    .select(
      `
      *,
      creator:profiles!contact_notes_created_by_fkey(full_name, email)
    `,
    )
    .single();

  if (error) {
    console.error("Error creating contact note:", error);
    throw error;
  }

  return data;
};

/**
 * Update an existing contact note
 */
export const updateContactNote = async (
  noteId: UUID,
  content: string,
): Promise<ContactNote> => {
  const { data, error } = await supabase
    .from("contact_notes")
    .update({ content })
    .eq("id", noteId)
    .select(
      `
      *,
      creator:profiles!contact_notes_created_by_fkey(full_name, email)
    `,
    )
    .single();

  if (error) {
    console.error("Error updating contact note:", error);
    throw error;
  }

  return data;
};

/**
 * Delete a contact note
 */
export const deleteContactNote = async (noteId: UUID): Promise<void> => {
  const { error } = await supabase
    .from("contact_notes")
    .delete()
    .eq("id", noteId);

  if (error) {
    console.error("Error deleting contact note:", error);
    throw error;
  }
};
